import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import escapeRegExp from "lodash/escapeRegExp";
import { useSelector } from "react-redux";

import Multimedia from "@sharedComponents/multimedia";
import { Panel, PanelCollapsibleArea, PanelLabel } from "./list-panel";
import SearchBar, { SearchBarRef } from "@stadium/ui/src/components/form/SearchBar";
import CheckboxInput from "@stadium/ui/src/components/form/CheckboxInput";
import FormLabel from "@sharedComponents/form-label";
import { ExternalButton, InternalButton } from "@sharedComponents/buttons";
import { getIsLockerShop, getStoreNumber } from "@selectors/store";

import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";

import { CATALOG_REQUEST_ITEM_TYPE_FORM_LINK } from "@stadium/shared/src/constants/stores/routes";
import { ANALYTICS_EVENT_ACTION_OPENED } from "@stadium/services/analytics/constants/default";
import {
  ANALYTICS_TARGET_FILTER_SECTION,
  ANALYTICS_ACTION_SECTION_COLLAPSED,
} from "@constants/analytics/filter";

import styles from "./main.module.scss";

import noResult from "@assets/images/svg/no-result.svg";

const MIN_ITEMS_FOR_SEARCH = 15;

interface ItemType extends Object {
  id: string;
  name: string;
  icon?: string;
  externalId?: string;
  options?: { name: string; id: string; label: React.ReactNode }[];
  logoSrc?: string;
  flagUrl?: string;
  label?: string;
}

const CheckboxList: React.FC<{
  id: string;
  // eslint-disable-next-line no-unused-vars
  onChange: (args: unknown) => unknown;
  items: ItemType[];
  label?: string;
  selectedIds: string[];
  disabled?: boolean;
  defaultOpen?: boolean;
  // eslint-disable-next-line no-unused-vars
  handleChecked: (
    key: string,
    id: string,
    callback: (args: unknown) => unknown,
    optionIds: string[],
    _e: ChangeEvent,
  ) => void;
  maxItemsToShow?: number;
  onViewAll?: React.ComponentProps<typeof InternalButton>["onClick"];
  placeholder?: string;
}> = ({
  id: key,
  items,
  onChange,
  label,
  selectedIds,
  handleChecked,
  disabled,
  defaultOpen,
  maxItemsToShow,
  onViewAll,
  placeholder,
}) => {
  const isLockerShop = useSelector(getIsLockerShop);
  const storeNumber = useSelector(getStoreNumber);
  const withAnalytics = useWithAnalytics();

  const [filteredItems, setFilteredItems] = useState(items);
  const [searchQuery, setSearchQuery] = useState("");
  const searchBarRef = useRef<SearchBarRef>();

  const filterItems = useCallback(
    (query: string) => {
      if (query) {
        const valRegex = new RegExp(escapeRegExp(query), "i");
        return setFilteredItems(() => items.filter(({ name }) => valRegex.test(name)));
      }
      return setFilteredItems(items);
    },
    [items],
  );

  // re-filter on change of items
  // use case - change of page/ added other filters so items in this list changed, etc.
  useEffect(() => {
    filterItems(searchBarRef.current?.value);
  }, [filterItems]);

  const hasItems = !!filteredItems.length;
  const itemsToShow = filteredItems.slice(0, maxItemsToShow || filteredItems.length);

  return (
    <Panel
      className={`${styles.dropdownFilter} ${styles.filterByDropdown}`}
      eventKey={key}
      defaultOpen={defaultOpen}
      onSelect={(selectedKey) => {
        if (!storeNumber) return;
        const isOpening = selectedKey === key;
        withAnalytics(undefined, {
          track: true,
          target: ANALYTICS_TARGET_FILTER_SECTION,
          action: isOpening ? ANALYTICS_EVENT_ACTION_OPENED : ANALYTICS_ACTION_SECTION_COLLAPSED,
          eventProperties: {
            shop_number: storeNumber,
            filter_type: key,
          },
        })();
      }}
    >
      {label && (
        <PanelLabel className={styles.panelLabel} disabled={disabled} id={key}>
          {label}
        </PanelLabel>
      )}
      <PanelCollapsibleArea eventKey={key} className={styles.checkListWrapper}>
        <div>
          {items.length > MIN_ITEMS_FOR_SEARCH && (
            <SearchBar
              id={`search-${key}`}
              name={`search-${key}`}
              placeholder={placeholder || `Search ${label}`}
              ref={searchBarRef}
              classes={styles.searchInput}
              className={`${styles.searchInputCustom} ${styles.mobileFilterSearch}`}
              styles={styles}
              onChange={({ value }) => {
                setSearchQuery(value);
                filterItems(value);
              }}
            />
          )}
          <div className={`${styles.listingPanel} listing-Panel`}>
            {hasItems && (
              <>
                {itemsToShow.map((item) => {
                  const { id, name, options = [], logoSrc, flagUrl, label } = item;

                  const optionParams = {
                    key: id,
                    items: options,
                    onChange,
                    selectedIds,
                    handleChecked,
                    disabled: false,
                    defaultOpen: true,
                    maxItemsToShow,
                  };
                  const optionIds = options.map(({ id }) => id);
                  const isParentSelected = selectedIds.some((selectedId) =>
                    [id, ...optionIds].includes(selectedId),
                  );
                  const showChildOptions = options.length > 1 && isParentSelected;
                  return (
                    <>
                      <CheckboxInput
                        label={
                          <FormLabel iconType="check">
                            {flagUrl && (
                              <Multimedia
                                src={flagUrl}
                                alt={name}
                                type="image"
                                width={24}
                                height={24}
                                isNextImage={false}
                                className={styles.filterFlagImage}
                              />
                            )}
                            {name}
                            {logoSrc && (
                              <div className={styles.coreImage}>
                                <Multimedia src={logoSrc} alt={label} type="image" />
                              </div>
                            )}
                          </FormLabel>
                        }
                        key={id}
                        id={id}
                        name={id}
                        checked={isParentSelected}
                        classes={styles.checkListItem}
                        onChange={({ _e }) => handleChecked(key, id, onChange, optionIds, _e)}
                      />
                      {showChildOptions ? <CheckboxList {...optionParams} id={id} /> : ""}
                    </>
                  );
                })}
                {!!maxItemsToShow && filteredItems.length > maxItemsToShow && (
                  <div className={`${styles.checkListItem} ${styles.viewAllLInk}`}>
                    <InternalButton styleType="link" onClick={onViewAll}>
                      View All
                    </InternalButton>
                  </div>
                )}
              </>
            )}

            {!hasItems && searchQuery && (
              <div className={styles.noResultCont}>
                <Multimedia type="image" src={noResult} alt="no result" height={60} width={60} />
                <div>
                  No results found under [{searchQuery}], try{" "}
                  <InternalButton
                    styleType="no-style"
                    className={styles.btnNoStyle}
                    onClick={searchBarRef.current?.reset}
                  >
                    resetting
                  </InternalButton>
                  your search
                  {isLockerShop ? (
                    <>
                      {", "} or{" "}
                      <ExternalButton
                        href={CATALOG_REQUEST_ITEM_TYPE_FORM_LINK}
                        styleType="no-style"
                        target="_blank"
                        className={styles.btnNoStyle}
                      >
                        Request Expert Swag Help
                      </ExternalButton>
                    </>
                  ) : (
                    "."
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </PanelCollapsibleArea>
    </Panel>
  );
};

export default CheckboxList;
